// Server state for Reconcile Wells. The reads are shared by both entry points — the
// Map Query wells table and the Well Workspace banner — which is why they live beside
// the sheet rather than on either page.

import { useMemo } from 'react';
import { useSetAtom } from 'jotai';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  queryOptions,
} from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { FeatureFlagWindow } from 'types/window';
import type {
  WellboreMapData,
  WellWithWellboresResult,
} from 'types/api-responses';
import { wellDedupeKeys } from '../../query-keys';
import { readWriteFailure, toWorkspaceError } from '../../utils';
import type {
  DuplicateWellCandidatesResponse,
  DuplicateWellCluster,
  ReconcileWellsResponse,
  WellPastVersion,
} from './types';
import { buildGeometryFromDetails, buildPoolFromClusters } from './pool-utils';
import { addReconciledUwisAtom } from '../../atoms';
import type {
  WellCandidate,
  WellReconcileAssignment,
} from './consolidation-utils';

const READ_RETRY = 1;

/**
 * Whether reconcile is enabled in this environment — mirrors the backend WELL_DEDUPE_RECONCILE_ENABLED
 * kill switch (exposed through JsConfig as window.CONFIG.featureFlags.wellDedupeReconcileEnabled). Shared
 * by both tearsheet entry points. Defaults ON when the config is absent (e.g. a test render), matching the
 * backend's default-on kill switch.
 * @return {boolean} True unless this environment has reconcile turned off.
 */
export const isReconcileEnabled = (): boolean =>
  (window as Window & Partial<FeatureFlagWindow>).CONFIG?.featureFlags
    ?.wellDedupeReconcileEnabled ?? true;

/** A detection run is rewritten wholesale, so a cached page stays good for a while. */
const CANDIDATES_STALE_MS = 5 * 60 * 1000;

const NO_CANDIDATES: DuplicateWellCandidatesResponse = {
  totalDuplicateCount: 0,
  countsByUWI: {},
  results: [],
};

const NO_CLUSTERS: DuplicateWellCluster[] = [];

// ── Requests ──

/**
 * Candidate duplicate clusters for a set of wells, off the latest completed detection
 * run. One call answers both questions the UI asks — which rows have duplicates
 * (`countsByUWI`) and what is in a given row's cluster — so a page of rows needs a
 * single request and clicking a row needs none.
 * @param {string[]} uwis The wells to look up.
 * @param {AbortSignal} [signal] Cancels the request.
 * @return {Promise<DuplicateWellCandidatesResponse>} Clusters, counts and the total.
 */
const fetchDuplicateWellCandidates = (
  uwis: string[],
  signal?: AbortSignal
): Promise<DuplicateWellCandidatesResponse> =>
  api
    .post('queries/duplicate_well_candidates', { json: { uwis }, signal })
    .json<DuplicateWellCandidatesResponse>();

// Query::Wells::Database clamps page_size to its max_records (100), so a single request can resolve at
// most 100 wells however large pageSize is. Clusters above 100 members exist (the detector's name rule
// produces some), and a member past the 100th would otherwise render blank, so split the lookup into
// 100-sized requests and merge.
const RECONCILE_DETAIL_PAGE_SIZE = 100;

/**
 * Attributes and wellbore geometry for the records in a cluster.
 *
 * The dedupe payload carries identity only (uwi, name), so the sheet's table
 * columns and its inset map come from the ordinary wells query instead.
 *
 * `use_elastic` is deliberately NOT sent: the Elasticsearch backend drops a reconciled
 * well unconditionally, while the Postgres one keeps it whenever the query carries a
 * uwi filter. A retired record has to stay resolvable here or the past-version rows
 * would render with no name and no geometry.
 * @param {string[]} uwis The records to resolve.
 * @param {AbortSignal} [signal] Cancels the request.
 * @return {Promise<WellWithWellboresResult[]>} One row per resolvable well.
 */
export const fetchReconcileWellDetails = async (
  uwis: string[],
  signal?: AbortSignal
): Promise<WellWithWellboresResult[]> => {
  const chunks: string[][] = [];
  for (let i = 0; i < uwis.length; i += RECONCILE_DETAIL_PAGE_SIZE) {
    chunks.push(uwis.slice(i, i + RECONCILE_DETAIL_PAGE_SIZE));
  }

  const pages = await Promise.all(
    chunks.map((chunk) =>
      api
        .post('queries/wells', {
          json: {
            uwi: chunk,
            includeDeviationSurvey2d: true,
            pageSize: chunk.length,
          },
          signal,
        })
        .json<{ results: WellWithWellboresResult[] }>()
    )
  );

  return pages.flatMap((body) => body.results ?? []);
};

/**
 * The records already reconciled into a survivor — the sheet's "Existing past
 * versions" list.
 * @param {string} uwi The surviving well.
 * @param {AbortSignal} [signal] Cancels the request.
 * @return {Promise<WellPastVersion[]>} The retired records, by uwi.
 */
const fetchWellPastVersions = async (
  uwi: string,
  signal?: AbortSignal
): Promise<WellPastVersion[]> => {
  const body = await api
    .get(`queries/wells/${encodeURIComponent(uwi)}/past_versions`, { signal })
    .json<{ results: WellPastVersion[] }>();

  return body.results ?? [];
};

/** Catalog rows per page in "Add wells to reconcile" — the wells query's default page size. */
export const CATALOG_PAGE_SIZE = 100;

/** One facet option and how many wells it holds under the other active filters. */
export interface CatalogFacetBucket {
  id: string;
  name: string;
  count: number;
}

/** What the Add-wells browse narrows the catalog by. */
export interface CatalogBrowseParams {
  /** Free text over name, uwi and operator; blank browses the whole catalog. */
  text: string;
  statusIds: string[];
  operatorIds: string[];
  page: number;
  pageSize: number;
}

interface WellCatalogResponse {
  results: WellWithWellboresResult[];
  totalCount?: number;
  facets?: {
    status?: { buckets: CatalogFacetBucket[] } | null;
    currentOperator?: { buckets: CatalogFacetBucket[] } | null;
  };
}

/**
 * One page of the well catalog for "Add wells to reconcile" — the operator is not limited to the
 * detector's suggestions. The same wells query the Map Query table uses: it returns the page, the
 * total, and the status and operator facet counts under every other active filter. Geometry is
 * left out; it is fetched for the picked wells only, when they are added.
 * @param {CatalogBrowseParams} params The text, facet selections and page.
 * @param {AbortSignal} [signal] Cancels the request.
 * @return {Promise<WellCatalogResponse>} The page of wells, the total and the facets.
 */
const fetchWellCatalog = async (
  { text, statusIds, operatorIds, page, pageSize }: CatalogBrowseParams,
  signal?: AbortSignal
): Promise<WellCatalogResponse> =>
  api
    .post('queries/wells', {
      json: {
        // Forces the Elasticsearch backend: with no text the query would otherwise route to
        // Postgres, which has no facets and ignores the status and operator filters.
        useElastic: true,
        text: text || undefined,
        statusIds: statusIds.length > 0 ? statusIds : undefined,
        currentOperatorIds: operatorIds.length > 0 ? operatorIds : undefined,
        page,
        pageSize,
      },
      signal,
    })
    .json<WellCatalogResponse>();

/**
 * Retire a sheet's duplicate sets into their survivors in ONE request. The endpoint commits every group
 * in a single transaction, so either all survivors are reconciled or none are — a refusal in a later
 * group never strands an earlier merge.
 * @param {WellReconcileAssignment[]} assignments The survivors and the records retiring into each.
 * @return {Promise<ReconcileWellsResponse>} The reconcile result (survivor + workspace per group);
 *   rejects (atomically) otherwise.
 */
const reconcileWells = (
  assignments: WellReconcileAssignment[]
): Promise<ReconcileWellsResponse> =>
  api
    .post('well_dedupe_reconciliations', {
      json: {
        groups: assignments.map((assignment) => ({
          survivor_uwi: assignment.survivorId,
          duplicate_uwis: assignment.retiredIds,
        })),
      },
    })
    .json<ReconcileWellsResponse>()
    .catch(async (error: unknown) => {
      throw await toWorkspaceError(error);
    });

// ── Reads ──

/**
 * Candidate duplicate clusters for a set of wells.
 *
 * One call per set of rows, not per row: the response carries both the per-well counts
 * the table reads to decide whether to offer the action, and the clusters the sheet
 * reads once an operator picks one.
 * @param {string[]} uwis The wells to look up; an empty list skips the request.
 * @return {object} The query, with `countsByUWI`, `results` and `totalDuplicateCount`.
 */
export const useDuplicateWellCandidates = (uwis: string[]) => {
  const query = useQuery(
    queryOptions({
      queryKey: wellDedupeKeys.candidates(uwis),
      queryFn: ({ signal }) => fetchDuplicateWellCandidates(uwis, signal),
      enabled: uwis.length > 0,
      retry: READ_RETRY,
      staleTime: CANDIDATES_STALE_MS,
    })
  );

  const data = query.data ?? NO_CANDIDATES;

  /**
   * Every cluster a well belongs to, empty when it has no open duplicates (or no uwi).
   * A well can sit in more than one (each rule groups its own member set), and
   * `countsByUWI` tallies peers across all of them, so the sheet needs them all. The
   * arrays are stable per response, so a caller can hand one straight to a memo.
   */
  const clustersFor = useMemo(() => {
    const byUwi = new Map<string, DuplicateWellCluster[]>();
    data.results.forEach((cluster) => {
      cluster.members.forEach((member) => {
        const clusters = byUwi.get(member.uwi);
        if (clusters) clusters.push(cluster);
        else byUwi.set(member.uwi, [cluster]);
      });
    });
    return (uwi: string | null | undefined): DuplicateWellCluster[] =>
      (uwi && byUwi.get(uwi)) || NO_CLUSTERS;
  }, [data.results]);

  return {
    countsByUWI: data.countsByUWI,
    totalDuplicateCount: data.totalDuplicateCount,
    clusters: data.results,
    clustersFor,
    isLoading: query.isLoading,
    error: query.error,
  };
};

export interface ReconcilePool {
  pool: WellCandidate[];
  geometry: Record<string, WellboreMapData>;
}

const EMPTY_POOL: ReconcilePool = { pool: [], geometry: {} };

/**
 * The records in a well's clusters, resolved into the sheet's pool and map geometry.
 *
 * Deferred until the sheet opens: the attributes and trajectories behind a cluster are
 * only worth fetching for the one cluster an operator actually looked at.
 * @param {string | null} anchorUwi The well the sheet was opened on.
 * @param {DuplicateWellCluster[]} clusters Its candidate clusters.
 * @param {boolean} enabled Whether the sheet is open.
 * @return {object} The pool, the geometry, and whether they are still loading.
 */
export const useReconcilePool = (
  anchorUwi: string | null,
  clusters: DuplicateWellCluster[],
  enabled: boolean
) => {
  const uwis = useMemo(
    () => [
      ...new Set(
        clusters.flatMap((cluster) =>
          cluster.members.map((member) => member.uwi)
        )
      ),
    ],
    [clusters]
  );

  const query = useQuery(
    queryOptions({
      queryKey: wellDedupeKeys.details(uwis),
      queryFn: ({ signal }) => fetchReconcileWellDetails(uwis, signal),
      enabled: enabled && uwis.length > 0,
      retry: READ_RETRY,
    })
  );

  const built = useMemo((): ReconcilePool => {
    if (clusters.length === 0 || !anchorUwi || !query.data) return EMPTY_POOL;
    return {
      pool: buildPoolFromClusters(clusters, query.data, anchorUwi),
      geometry: buildGeometryFromDetails(query.data),
    };
  }, [clusters, anchorUwi, query.data]);

  return {
    ...built,
    isLoading: query.isLoading && enabled && uwis.length > 0,
    error: query.error,
  };
};

/**
 * The records already reconciled into a well.
 * @param {string | null} uwi The surviving well.
 * @param {boolean} enabled Whether the sheet showing them is open.
 * @return {object} The query, with the past versions as `pastVersions`.
 */
export const useWellPastVersions = (uwi: string | null, enabled: boolean) => {
  const query = useQuery(
    queryOptions({
      queryKey: wellDedupeKeys.pastVersions(uwi ?? ''),
      queryFn: ({ signal }) => fetchWellPastVersions(uwi!, signal),
      enabled: enabled && Boolean(uwi),
      retry: READ_RETRY,
    })
  );

  return {
    pastVersions: query.data ?? [],
    isLoading: query.isLoading,
  };
};

/**
 * The catalog browse behind "Add wells to reconcile": page 1 loads as soon as the sheet opens, then
 * narrows by search text, facet selections and page. The previous page stays on screen while the
 * next loads so the facet lists do not flash empty. The caller debounces the text it passes in.
 * @param {CatalogBrowseParams} params The (already-debounced) text, facet selections and page.
 * @param {boolean} enabled Whether the Add-wells tearsheet is open.
 * @return {object} The page as `results`, the `totalCount`, and the status and operator facets.
 */
export const useWellCatalogBrowse = (
  params: CatalogBrowseParams,
  enabled: boolean
) => {
  const trimmed = { ...params, text: params.text.trim() };
  const query = useQuery(
    queryOptions({
      queryKey: wellDedupeKeys.catalogBrowse(trimmed),
      queryFn: ({ signal }) => fetchWellCatalog(trimmed, signal),
      enabled,
      retry: READ_RETRY,
      staleTime: CANDIDATES_STALE_MS,
      placeholderData: keepPreviousData,
    })
  );

  return {
    results: query.data?.results ?? [],
    totalCount: query.data?.totalCount ?? 0,
    statusBuckets: query.data?.facets?.status?.buckets ?? [],
    operatorBuckets: query.data?.facets?.currentOperator?.buckets ?? [],
    isLoading: query.isFetching,
    error: query.error,
  };
};

/**
 * What to tell the Insights user about a refused reconcile. The commit is atomic, so a refusal leaves
 * nothing applied — the message is simply the server's reason (or a fallback), as everywhere else in the
 * workspace.
 * @param {unknown} error The mutation's error, or null.
 * @return {string | null} The message, or null when nothing failed.
 */
export const reconcileFailureText = (error: unknown): string | null => {
  if (!error) return null;

  return readWriteFailure(error, 'The reconcile could not be completed.');
};

/** The committed structure plus the reconcile response's groups (survivor + workspace per group). */
export interface ReconcileWellsResult {
  assignments: WellReconcileAssignment[];
  groups: ReconcileWellsResponse['groups'];
}

/**
 * Commits a sheet's structure.
 *
 * The sheet can author several survivors; this posts them all in ONE request and the endpoint commits
 * them in a single transaction, so either every survivor is reconciled or none is — a refusal cannot
 * leave a half-applied structure. On success the retired records are marked reconciled; on both paths the
 * counts, clusters and survivor past-versions are refreshed (they all move on a reconcile).
 * @return {object} The mutation.
 */
export const useReconcileWells = () => {
  const queryClient = useQueryClient();
  const addReconciled = useSetAtom(addReconciledUwisAtom);

  return useMutation<ReconcileWellsResult, Error, WellReconcileAssignment[]>({
    retry: false,
    mutationFn: async (assignments) => {
      const response = await reconcileWells(assignments);
      return { assignments, groups: response.groups };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: wellDedupeKeys.all });
    },
    onSuccess: ({ assignments }) => {
      addReconciled(assignments.flatMap((assignment) => assignment.retiredIds));
    },
  });
};
